import type { GeneratorTemplate, GeneratorTemplateOptions, PromptOptionSnippet } from '../TeoApi';

export interface DropdownCategoryGrouped {
  id: string;
  label: string;
  help: string;
  order: number;

  groups: {
    name: string;

    dropdown: {
      id: string;
      options: {
        label: string;
        value: string;
        type: 'ACTIVE' | 'INACTIVE' | 'HIDDEN' | 'SELECTED';
      }[];
    };
  }[];
}

export const CATEGORIES: Record<string, { label: string; help: string; order: number }> = {
  department: {
    label: 'Bereich',
    help: '',
    order: 1,
  },
  purpose: {
    label: 'Ziel des Textes',
    help: 'Was soll der Text bewirken?',
    order: 2,
  },
  audience: {
    label: 'Zielgruppe',
    help: 'Wähle die Zielgruppe.',
    order: 3,
  },
  medium: {
    label: 'Medium',
    help: 'Wähle den Ausspielkanal. (Print/Digital und jeweiliges Format.)',
    order: 4,
  },
  style: {
    label: 'Stil',
    help: 'Formeller oder informeller Sprachstil?',
    order: 5,
  },
  tone: {
    label: 'Tonalität',
    help: 'Grundstimmung und Haltung des Textes.',
    order: 6,
  },
  type: {
    label: 'Textgattung',
    help: '',
    order: 7,
  },
} as const;

const dropdownCategoriesCache: Map<GeneratorTemplateOptions, DropdownCategoryGrouped[]> = new Map();
export function optionsToDropdownCategories(
  options?: GeneratorTemplateOptions,
): DropdownCategoryGrouped[] {
  if (!options) {
    return [];
  }
  const cached = dropdownCategoriesCache.get(options);
  if (cached) return cached;

  const dropdowns = [] as DropdownCategoryGrouped[];

  Object.entries(options).forEach(([categoryId, value]) => {
    const category = CATEGORIES[categoryId];
    if (!category) {
      return;
    }
    // divide in groups
    const groups = value.reduce(
      (acc, option) => {
        const subCategory = option.sort_category ?? '';
        let group = acc[subCategory];
        if (!group) {
          acc[subCategory] = [];
          group = acc[subCategory];
        }
        group.push(option);
        return acc;
      },
      {} as Record<string, PromptOptionSnippet[]>,
    );

    const result = Object.entries(groups).map(([subCategory, options]) => {
      return {
        name: subCategory,
        dropdown: {
          id: categoryId,
          options: options.map((option) => ({
            label: option.title,
            value: String(option.id),
            type: 'ACTIVE' as const,
          })),
        },
      };
    });
    result.sort((a, b) => a.name.localeCompare(b.name));
    dropdowns.push({
      id: categoryId,
      label: category.label ?? categoryId,
      help: category.help ?? '',
      order: category.order ?? 999,
      groups: result,
    });
  });

  dropdowns.sort((a, b) =>
    a.order === b.order ? a.label.localeCompare(b.label) : a.order - b.order,
  );

  dropdownCategoriesCache.set(options, dropdowns);
  return dropdowns;
}

export function processSelectedOptionsForTemplate(
  template: GeneratorTemplate,
  selectedOptions: Record<string, string | undefined>,
) {
  const dropdownCategories = optionsToDropdownCategories(template.options);
  for (const category of dropdownCategories) {
    const optionsValues = category.groups
      .flatMap((group) => group.dropdown.options)
      .map((option) => option.value);
    if (optionsValues.length === 1 && optionsValues[0]) {
      selectedOptions[category.id] = String(optionsValues[0]);
    }
    const selected = String(selectedOptions[category.id]);
    if (selected && !optionsValues.includes(selected)) {
      selectedOptions[category.id] = undefined;
    }
  }
  return selectedOptions;
}

export function cleanSelectedOptionsForTemplate(
  template: GeneratorTemplate | null | undefined,
  selectedOptions: Record<string, string | undefined>,
): Record<string, string> {
  if (!template) {
    return {};
  }
  const dropdownCategories = optionsToDropdownCategories(template.options);
  const cleanSelected = {} as Record<string, string>;
  for (const category of dropdownCategories) {
    const optionsValues = category.groups
      .flatMap((group) => group.dropdown.options)
      .map((option) => option.value);
    if (optionsValues.length === 1 && optionsValues[0]) {
      cleanSelected[category.id] = optionsValues[0];
    }
    const selected = selectedOptions[category.id];
    if (selected && optionsValues.includes(selected)) {
      cleanSelected[category.id] = selected;
    }
  }
  return cleanSelected;
}
